import { useMemo } from "react";
import {
  PartiesChildrenEditor,
  computeMergeSuggestions,
  type EditorPersonInput,
  type NameExcerpt,
  type PartiesChildrenEditorState,
} from "@app/components/PartiesChildrenEditor";
import type { ClassifyEntitiesResponse } from "../lib/api";

export type GroundingState = PartiesChildrenEditorState;

/** Seed the shared editor's cluster inputs from the classifier result. Mirrors
 *  desktop's PeoplePane: on first entry, fold the matched nicknames in as "also
 *  called" aliases (by canonical name) and derive merge suggestions (a nickname
 *  whose alias is ALSO a separately-detected child). On resume, rebuild from the
 *  user's prior grounding so edits survive Back/Forward. Pure → unit-testable. */
export function seedFromClassify(
  result: ClassifyEntitiesResponse,
  initialGrounding?: GroundingState | null,
): {
  partyAInput: EditorPersonInput;
  partyBInput: EditorPersonInput;
  childrenInput: EditorPersonInput[];
  suggestions: ReturnType<typeof computeMergeSuggestions>;
} {
  const { party_a: partyA, party_b: partyB } = result;
  // `nicknames` and `children` are optional in practice, not just in type: a
  // classify payload that omits either used to take the whole Parties step
  // down with a TypeError, leaving a blank panel and no way forward. Seeding
  // from an empty list degrades to "no aliases found", which is recoverable —
  // the user can still name people by hand.
  const nicknames = result.nicknames ?? [];
  const children = result.children ?? [];
  const countFor = (alias: string, canonical: string) =>
    nicknames.find((n) => n.alias === alias && n.canonical === canonical)
      ?.mention_count;
  // The child's own mention count → drives "most-frequent name wins" on merge.
  const childCountFor = (name: string) =>
    children.find((c) => c.name === name)?.mention_count;

  if (initialGrounding) {
    const g = initialGrounding;
    const aliasInputs = (
      canon: string,
      fromParty: boolean,
    ): EditorPersonInput["aliases"] =>
      ((fromParty ? g.partyAliases : g.childAliases)[canon] ?? []).map((t) => ({
        text: t,
        count: countFor(t, canon),
      }));
    return {
      partyAInput: { name: partyA, aliases: aliasInputs(partyA, true) },
      partyBInput: { name: partyB, aliases: aliasInputs(partyB, true) },
      childrenInput: g.children.map((name) => ({
        name,
        count: childCountFor(name),
        aliases: aliasInputs(name, false),
      })),
      suggestions: [],
    };
  }

  const aliasesFor = (canon: string): EditorPersonInput["aliases"] =>
    nicknames
      .filter((n) => n.canonical === canon)
      .map((n) => ({ text: n.alias, count: n.mention_count }));
  const childNames = children.map((c) => c.name);
  return {
    partyAInput: { name: partyA, aliases: aliasesFor(partyA) },
    partyBInput: { name: partyB, aliases: aliasesFor(partyB) },
    childrenInput: children.map((c) => ({
      name: c.name,
      count: c.mention_count,
      aliases: aliasesFor(c.name),
    })),
    suggestions: computeMergeSuggestions(
      childNames,
      nicknames.map((n) => ({ alias: n.alias, canonical: n.canonical })),
    ),
  };
}

interface Props {
  result: ClassifyEntitiesResponse;
  /** Persisted grounding from a prior visit to this step (resume). */
  initialGrounding?: GroundingState | null;
  /** Fired on every edit with the structured grounding; the host persists it. */
  onGroundingChange?: (state: GroundingState) => void;
  /** Client-side corpus search → real message excerpts for a name. Lets the
   *  editor offer "verify against the messages" on low-hit nicknames, same as
   *  desktop — here it runs entirely in the browser (no server, no spend). */
  getExcerpts?: (term: string) => Promise<NameExcerpt[]>;
}

/**
 * Cloud step 2 — confirm parties + children + nicknames. A thin wrapper over the
 * shared {@link PartiesChildrenEditor}, mirroring desktop's PeoplePane: the user
 * lands directly on the editor (no summary gate), so they always see and confirm
 * the matched nicknames. The pane's heading + lede are rendered by the host
 * (App's step chrome); this owns only the seed adapter + the editor.
 */
export function ConfirmPeoplePanel({
  result,
  initialGrounding,
  onGroundingChange,
  getExcerpts,
}: Props) {
  // Seed ONCE per mount (the editor is uncontrolled after); resume works because
  // App remounts this with initialGrounding when the user navigates back.
  const seed = useMemo(
    () => seedFromClassify(result, initialGrounding),
    [result, initialGrounding],
  );

  return (
    <div className="pce-host">
      <PartiesChildrenEditor
        partyA={seed.partyAInput}
        partyB={seed.partyBInput}
        initialChildren={seed.childrenInput}
        suggestions={seed.suggestions}
        getExcerpts={getExcerpts}
        // Per-party pronouns (optional, clearable). The choice rides the
        // grounding state, so resume restores it like the aliases.
        pronouns={{ initial: initialGrounding?.partyPronouns ?? {} }}
        onChange={onGroundingChange}
      />
    </div>
  );
}
